import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { FeatureCheckmarkTransparent } from '../../assets/FeatureCheckmarkTransparent'

// M2.5: the tooltip is centred on its trigger, so on a narrow screen a trigger near
// either edge pushed it off-screen. Keep this in step with the page's px-6 gutter.
const TOOLTIP_MARGIN = 24

const FEATURE_SECTIONS = [
  {
    title: 'Highlights',
    items: [
      {
        label: 'Properties Per Month',
        tooltip:
          'This limit refers to the number of unique properties you can add to your pipeline each month. Once added, you can analyze each property as many times as you want.',
        starter: '20', pro: '250', ultimate: 'Unlimited',
      },
      {
        label: 'ARV Comps',
        tooltip:
          'Starter includes the top 10 highest-ranked comparable sales. Pro and Ultimate unlock all available comparable sales for deeper market analysis.',
        starter: 'Top 10', pro: 'All Available', ultimate: 'All Available',
      },
      {
        label: 'Custom Templates',
        tooltip:
          'Save custom deal analysis templates to quickly reuse financing assumptions, costs, and projections across future deals.',
        starter: '2', pro: 'Unlimited', ultimate: 'Unlimited',
      },
      {
        label: 'Users',
        tooltip:
          'Need more seats? Additional users are $299/year each, billed annually and prorated. Add or remove users anytime in Account Settings.',
        starter: '1', pro: '1', ultimate: '4 Included / Add More',
      },
    ],
  },
  {
    title: 'Deal Analysis',
    items: [
      {
        label: 'Flip & BRRRR Projection',
        tooltip: (
          <>
            <p className="font-semibold mb-1">Includes:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Deal scoring</li>
              <li>Scenario modeling</li>
              <li>Financing structures</li>
              <li>Sales proceeds</li>
              <li>Custom templates</li>
            </ul>
          </>
        ),
        starter: true, pro: true, ultimate: true,
      },
      {
        label: 'ARV Analysis',
        tooltip: (
          <>
            <p className="font-semibold mb-1">Includes:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Verified comps</li>
              <li>Advanced comp scoring</li>
              <li>DOM trends</li>
              <li>Automatic value adjustments</li>
              <li>Listing history &amp; photos</li>
            </ul>
          </>
        ),
        starter: true, pro: true, ultimate: true,
      },
      { label: 'Rent Comps', badge: 'COMING SOON', starter: true, pro: true, ultimate: true },
      { label: 'Comp Scoring', starter: true, pro: true, ultimate: true },
      { label: 'Deal Scoring', starter: true, pro: true, ultimate: true },
    ],
  },
  {
    title: 'Deal Pipeline',
    items: [
      { label: 'Deal Tracking', starter: true, pro: true, ultimate: true },
      { label: 'Property Dashboard', starter: true, pro: true, ultimate: true },
      { label: 'Property Data & History', starter: true, pro: true, ultimate: true },
      { label: 'Custom Tags', starter: true, pro: true, ultimate: true },
      { label: 'Notes', starter: true, pro: true, ultimate: true },
    ],
  },
  {
    title: 'Deal Presentations',
    items: [
      { label: 'PDF Reports', badge: 'COMING SOON', starter: true, pro: true, ultimate: true },
    ],
  },
]

const HelpTooltip = ({ content }) => {
  const [open, setOpen] = useState(false)
  // Horizontal correction applied on top of the -50% centring, in px.
  const [shift, setShift] = useState(0)
  const wrapRef = useRef(null)
  const tipRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDocPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDocPointer)
    return () => document.removeEventListener('pointerdown', onDocPointer)
  }, [open])

  // Set #4, Desktop 4: appear on cursor hover, no click required. Gated to devices that
  // actually have a pointer for the same reason as the M2.6 colour fix above — a touch tap
  // fires a synthetic mouseenter with no matching mouseleave, so wiring hover unconditionally
  // would leave the tooltip stuck open after the finger lifts. On a hover-capable device a
  // real pointer click force-opens rather than toggles, so clicking the trigger while it's
  // already open on hover can't fight the hover state and instantly re-close it.
  const hoverCapable = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches
  const handleMouseEnter = () => { if (hoverCapable()) setOpen(true) }
  const handleMouseLeave = () => { if (hoverCapable()) setOpen(false) }

  // M2.5: measure once the tooltip is on screen and pull it back inside the gutter.
  // Measuring happens with shift already reset to 0, so the correction is absolute
  // rather than accumulating each time the tooltip is reopened.
  useLayoutEffect(() => {
    if (!open) {
      setShift(0)
      return
    }
    const el = tipRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const overflowLeft = TOOLTIP_MARGIN - rect.left
    const overflowRight = rect.right - (window.innerWidth - TOOLTIP_MARGIN)
    if (overflowLeft > 0) setShift(overflowLeft)
    else if (overflowRight > 0) setShift(-overflowRight)
  }, [open])

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        aria-label="More info"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          // Enter/Space activation dispatches a click with detail 0 (no real pointer
          // click happened), which is how this tells a keyboard toggle apart from a
          // mouse click without a separate onKeyDown — a keyboard user needs Enter to
          // close what Enter opened, since nothing else can reach a force-opened panel.
          const detail = e.detail ?? e.nativeEvent?.detail
          if (detail === 0) { setOpen((v) => !v); return }
          if (hoverCapable()) { setOpen(true); return }
          setOpen((v) => !v)
        }}
        /* M2.6: the active colour used to come from :hover, which sticks on a touch
           screen — so closing the tooltip left the trigger green until you tapped
           somewhere else. The colour now follows `open`, and :hover is limited to
           devices that actually have a pointer. */
        className={`w-[16px] h-[16px] flex items-center justify-center transition-colors [@media(hover:hover)]:hover:text-brand-primary ${
          open ? 'text-brand-primary' : 'text-brand-disabled'
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6.5" stroke="currentColor" />
          <text x="7" y="10" textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="600">?</text>
        </svg>
      </button>
      <span
        ref={tipRef}
        role="tooltip"
        style={{ transform: `translateX(calc(-50% + ${shift}px))` }}
        className={`absolute left-1/2 bottom-full mb-2 w-[260px] rounded-lg bg-brand-dark text-white text-[12px] leading-[18px] p-3 shadow-lg transition-opacity z-30 text-left ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {content}
      </span>
    </span>
  )
}

const renderCell = (value, emphasised) => {
  if (value === true) {
    return (
      <div className="flex items-center justify-center">
        <div className="w-[28px] h-[28px] rounded-full bg-[#CBF7B7] flex items-center justify-center">
          <FeatureCheckmarkTransparent />
        </div>
      </div>
    )
  }
  if (value === false) {
    return <span className="text-[18px] font-medium text-brand-badgeMutedDark">—</span>
  }
  // D2.5.2: `md:font-bold` rather than plain `font-bold` — hover has no meaning below md,
  // and on a touch screen mouseenter fires on tap and never clears, which is exactly how
  // the tooltip trigger got stuck green in M2.6.
  return (
    <span className={`text-[16px] text-brand-dark transition-[font-weight] ${emphasised ? 'font-medium md:font-bold' : 'font-medium'}`}>
      {value}
    </span>
  )
}

// M2.2.1: the Starter/Pro/Ultimate tab strip that used to sit here is gone. Below md the
// three price columns are still too cramped to show at once, so the table still shows one
// plan at a time — but which one is now decided by the card you have swiped to in
// PlanCarousel, which is why `selectedPlan` arrives as a prop instead of living here.
const PlanFeatures = ({ selectedPlan, hoveredPlan }) => {
  // Selected column shows on mobile; all three show from md up.
  const colClass = (id) =>
    `${selectedPlan === id ? 'flex' : 'hidden'} md:flex justify-center`

  // D2.5.2 covers the Highlights block only — that is the section he named, and it is the
  // one with values worth comparing rather than rows of identical checkmarks.
  const emphasised = (sectionTitle, id) =>
    sectionTitle === 'Highlights' && hoveredPlan === id

  return (
    // D2.5: the table is 1040px wide — three 242px plan columns and a 242px label column
    // with 24px gaps (242×4 + 24×3 = 1040). The page gutter is 120px each side, leaving
    // only 960px, so the table steps 40px outside it on each side to reach its width.
    //
    // Review #3, M1.2: 24px from the cards to Highlights on mobile. The carousel track keeps an
    // 8px bottom pad for the hidden scrollbar, so 16 here is what measures 24 from the card edge.
    // Desktop is untouched — he filed this under Mobile.
    <section className="w-full mt-[16px] md:mt-[56px] md:w-[1040px] md:-mx-[40px]">
      {/* last:mb-0 — the gap to the FAQs is owned by PricingFaq's mt, not stacked on top of it */}
      {FEATURE_SECTIONS.map((section) => (
        <div key={section.title} className="mb-[44px] last:mb-0">
          <div className="pb-4 border-b border-brand-fieldBdr">
            {/* Review #3, M1.3: extra-bold, both widths. 800 comes from his word "extra-bold" —
                the Figma read endpoints were rate-limited when this was built, and README's rule is
                that a value he states in words outranks a frame. Flagged in the reply as his to
                correct if the file says 700. */}
            <h2 className="text-[20px] text-left font-extrabold text-brand-dark">
              {section.title}
            </h2>
          </div>

          <div>
            {section.items.map((item) => (
              <div
                key={item.label}
                className="grid grid-cols-[2fr_1fr] md:grid-cols-[1fr_242px_242px_242px] md:gap-x-[24px] items-center min-h-[58px] border-b border-brand-fieldBdr"
              >
                <div className="flex items-center gap-2 flex-wrap text-left pr-2">
                  <span className="text-[14px] md:text-[16px] font-medium text-brand-dark text-left">
                    {item.label}
                  </span>
                  {item.tooltip && <HelpTooltip content={item.tooltip} />}
                  {item.badge && (
                    <div className="h-[20px] px-2 rounded-[6px] bg-brand-primary flex items-center justify-center shrink-0">
                      <span className="text-white text-[10px] font-bold tracking-wide whitespace-nowrap">
                        {item.badge}
                      </span>
                    </div>
                  )}
                </div>

                <div data-col="starter" className={colClass('starter')}>{renderCell(item.starter, emphasised(section.title, 'starter'))}</div>
                <div data-col="pro" className={colClass('pro')}>{renderCell(item.pro, emphasised(section.title, 'pro'))}</div>
                <div data-col="ultimate" className={colClass('ultimate')}>{renderCell(item.ultimate, emphasised(section.title, 'ultimate'))}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}

export default PlanFeatures
